import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

const FREQ_OPTIONS = [
  { value: "quarterly", label: "Quarterly (default)", n: 4 },
  { value: "monthly", label: "Monthly", n: 12 },
  { value: "half-yearly", label: "Half-Yearly", n: 2 },
  { value: "annually", label: "Annually", n: 1 },
];

function fdFV(P, annualRatePct, nFreq, years) {
  if (P <= 0 || years <= 0) return P;
  return P * Math.pow(1 + annualRatePct / (100 * nFreq), nFreq * years);
}

export default function FdCalculator() {
  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(5);
  const [months, setMonths] = useState(0);
  const [freq, setFreq] = useState("quarterly");

  const nFreq = FREQ_OPTIONS.find((f) => f.value === freq)?.n ?? 4;
  const totalYears = (Number(years) || 0) + (Number(months) || 0) / 12;

  const { fv, interest, principalPct, interestPct, rows } = useMemo(() => {
    const P = Number(amount) || 0;
    const fv = fdFV(P, Number(rate) || 0, nFreq, totalYears);
    const interest = fv - P;
    const principalPct = fv > 0 ? (P / fv) * 100 : 0;
    const interestPct = 100 - principalPct;
    const wholeYears = Math.floor(totalYears);
    const rows = [];
    for (let k = 1; k <= wholeYears; k++) {
      const fvk = fdFV(P, Number(rate) || 0, nFreq, k);
      rows.push({
        year: k,
        primary: fvk,
        segments: [
          { value: P, className: "bg-ink", legendClassName: "bg-ink", label: "Deposit" },
          { value: Math.max(0, fvk - P), className: "bg-brass", legendClassName: "bg-brass", label: "Interest" },
        ],
        columns: [{ value: formatINR(fvk) }],
      });
    }
    // Partial final row if tenure includes extra months
    if (totalYears > wholeYears) {
      rows.push({
        year: `${wholeYears}y ${Math.round((totalYears - wholeYears) * 12)}m`,
        primary: fv,
        segments: [
          { value: P, className: "bg-ink", legendClassName: "bg-ink", label: "Deposit" },
          { value: Math.max(0, interest), className: "bg-brass", legendClassName: "bg-brass", label: "Interest" },
        ],
        columns: [{ value: formatINR(fv) }],
      });
    }
    return { fv, interest, principalPct, interestPct, rows };
  }, [amount, rate, nFreq, totalYears]);

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <NumField label="Deposit Amount (₹)" value={amount} onChange={setAmount} min={0} testId="fd-amount" />
          <NumField label="Interest Rate (% per annum)" value={rate} onChange={setRate} step={0.1} min={0} max={20} testId="fd-rate" />

          <div className="grid grid-cols-2 gap-4">
            <NumField label="Tenure — Years" value={years} onChange={setYears} min={0} max={10} testId="fd-years" />
            <NumField label="Tenure — Months" value={months} onChange={setMonths} min={0} max={11} testId="fd-months" />
          </div>

          <label className="block">
            <span className="eyebrow block mb-2">Compounding Frequency</span>
            <select
              value={freq}
              onChange={(e) => setFreq(e.target.value)}
              className="fld"
              data-testid="fd-freq"
            >
              {FREQ_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <p className="text-[11px] text-ink-soft/85 leading-relaxed mt-2">
              Most Indian bank FDs compound interest quarterly.
            </p>
          </label>
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="fd-result">
          <div className="eyebrow mb-2">Maturity Value</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink">
            <LedgerNumber value={fv} testId="fd-future-value" />
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-2 eyebrow mb-2">
              <span>Deposit vs. Interest</span>
            </div>
            <div className="w-full h-3 rounded-sm overflow-hidden border hairline flex">
              <div style={{ width: `${principalPct}%` }} className="bg-ink transition-all duration-500" />
              <div style={{ width: `${interestPct}%` }} className="bg-brass transition-all duration-500" />
            </div>
            <div className="flex justify-between mt-2 text-[12px] text-ink-soft">
              <span><span className="inline-block w-2 h-2 bg-ink mr-1.5" /> Deposit · {principalPct.toFixed(0)}%</span>
              <span><span className="inline-block w-2 h-2 bg-brass mr-1.5" /> Interest · {interestPct.toFixed(0)}%</span>
            </div>
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <Row label="Deposit Amount" value={formatINR(amount)} />
            <Row label="Total Interest" value={formatINR(interest)} accent />
            <Row label="Maturity Value" value={formatINR(fv)} />
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <YearBreakdown
          rows={rows}
          primaryLabel="Value at end of each year"
          tableHeaders={["Year", "Value at End of Year"]}
          testIdPrefix="fd"
        />
      )}

      <CalculatorActions
        testIdPrefix="fd"
        withStrategyCta
        buildPayload={() => {
          const tenureText = `${years}y ${months}m`;
          const freqLabel = FREQ_OPTIONS.find((f) => f.value === freq)?.label || freq;
          return {
            type: "fd",
            title: "FD Calculator",
            one_liner: `an FD maturity calculation — ${formatINR(amount)} at ${rate}% for ${tenureText}, compounded ${freqLabel.toLowerCase()}`,
            inputs: [
              { label: "Deposit Amount", value: formatINR(amount) },
              { label: "Interest Rate", value: `${rate}% p.a.` },
              { label: "Tenure", value: tenureText },
              { label: "Compounding", value: freqLabel },
            ],
            hero: { label: "Maturity Value", value: formatINR(fv) },
            summary: [
              { label: "Deposit Amount", value: formatINR(amount) },
              { label: "Total Interest", value: formatINR(interest), accent: true },
            ],
            row_headers: ["Year", "Value at End of Year"],
            rows: rows.map((r) => [String(r.year), formatINR(r.primary)]),
          };
        }}
      />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function NumField({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}
